import OnlineAppComponent from "../../lib/online_app_component";
import {scrollIntoView} from "scroll-js";
import {getThrottledFunction} from "../../../helpers/throttle";
import {OnlineApp} from "../../online_app";
import {OnlineAppNavConfig} from "../online_app_nav";

export const OnlineAppRootConfig = {
    componentTypeKey: 'OnlineAppRoot',
    baseHtmlClass: 'online_app',
    selector: '.online_app',

    datasetConfigKey: 'online_app_root__config',

    modalModeCode: 'MODAL',
    oversizeModalModeCode: 'OVERSIZE_MODAL',

    marginForNav: 100, // The margin on the right side of the nav element that we allow when determining the size for what is "modal".
}

export class OnlineAppRoot extends OnlineAppComponent {

    static get config() {
        return OnlineAppRootConfig;
    }

    constructor(id_or_element) {
        super(id_or_element, null, OnlineAppRootConfig);

        this.setDatasetConfig(OnlineAppRoot.config.datasetConfigKey);

        this.onlineApp = this.getOnlineApp();

        const resizeHandler = getThrottledFunction(this.handleResize.bind(this), 100);
        this.observer = new ResizeObserver(resizeHandler);
        this.observer.observe(this.element);

        // Listen for the nav being constructed so we can determine the modal mode based on the nav's width.
        this.onlineApp.doc.addEventListener(OnlineAppNavConfig.constructedEventName, this.handleNavConstructed.bind(this));

        this.render();
    }

    getOnlineApp() {
        const onlineAppConfig = { ...this.datasetConfig }; // Clone the config before modding.
        onlineAppConfig.basePageTitle = document.title;
        onlineAppConfig.window = window;
        onlineAppConfig.document = document;
        return new OnlineApp(this, onlineAppConfig);
    }

    handleResize() {
        this.handleModalMode();
    }

    handleNavConstructed() {
        this.handleModalMode();
    }

    getWidth() {
        return this.element.offsetWidth;
    }

    handleModalMode() {
        const modeCode = this.getModeCode();
        this.dispatchAction('viewportModal', modeCode);
    }

    getModeCode() {
        if (this.isModalSized()) return OnlineAppRootConfig.modalModeCode;
        if (this.isOversizeModalSized()) return OnlineAppRootConfig.oversizeModalModeCode;
        return null;
    }

    // We determine the maximum width for what is considered "modal" by looking at the nav element's width and adding a margin.
    // Anything at or below this is considered "modal".
    getModalMaxWidth() {
        let navMaxWidth = (!!this.onlineApp?.onlineAppNav) ? this.onlineApp.onlineAppNav.getMaxWidth() : 0;
        return navMaxWidth + OnlineAppRootConfig.marginForNav;
    }

    isModalSized() {
        return(this.getWidth() <= this.getModalMaxWidth());
    }

    // Oversize modal is when the element's width is a bit bigger than "modal" (it's oversize).
    // The "bit bigger" is a fraction of the modal max width.
    getOversizeModalMaxWidth() {
        const modalMaxWidth = this.getModalMaxWidth();
        return Math.floor(modalMaxWidth * 1.25);
    }

    isOversizeModalSized() {
        const width = this.getWidth();
        return (width > this.getModalMaxWidth()) && (width <= this.getOversizeModalMaxWidth());
    }

    scrollTo() {
        this.isScrollingTo = true;
        scrollIntoView(this.element, document.body, {behavior: 'smooth'}).then(() => {
            scrollIntoView(this.element, document.body).then(() => {
                this.isScrollingTo = false
            });
        });
    }

    renderAuxOpenState(state) {
        if (state) {
            this.addHtmlClassState('auxOpen');
            this.removeHtmlClassState('auxClosed');
        } else {
            this.removeHtmlClassState('auxOpen');
            this.addHtmlClassState('auxClosed');
        }
    }

    renderInitializingState(state) {
        this.conditionallySetHtmlClassState(state, 'initializing');
    }

    renderBootingState(state) {
        state ? this.addHtmlClassState('booting') : this.removeHtmlClassState('booting');
    }

    renderNavOpenState(state) {
        state ? this.addHtmlClassState('nav_open') : this.removeHtmlClassState('nav_open');
    }

    renderModalState(modeCode) {
        (modeCode === OnlineAppRootConfig.modalModeCode) ? this.addHtmlClassState('modal') : this.removeHtmlClassState('modal');
        (modeCode === OnlineAppRootConfig.oversizeModalModeCode) ? this.addHtmlClassState('oversize_modal') : this.removeHtmlClassState('oversize_modal');
    }

    render() {
        const auxResourceUrl = this.readState('auxResourceUrl');
        this.renderInitializingState(this.readState('initializing'));
        this.renderBootingState(this.readState('booting'));
        this.renderAuxOpenState(!!auxResourceUrl);
        this.renderNavOpenState(this.readState('navOpen'))
        this.renderModalState(this.readState('modal'));
        !this.readStateMachineValue('mapFilter') ? this.addHtmlClassState('no_map_filter') : this.removeHtmlClassState('no_map_filter');
    }

}